'use client';

import { LeverageValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: LeverageValues;
  onValuesChange: (field: keyof LeverageValues, value: number | string) => void;
  translations: ToolsTranslations['leverage'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof LeverageValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof LeverageValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Security Requirement */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="security-requirement-input" className="text-gray-700 font-medium">{translations.securityRequirement}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="security-requirement-input"
              type="text"
              value={values.securityRequirement.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('securityRequirement', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.securityRequirement}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.securityRequirement / 10000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="security-requirement-slider"
            type="range"
            min="100"
            max="10000"
            step="10"
            value={values.securityRequirement}
            onChange={(e) => handleSliderChange('securityRequirement', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.securityRequirement} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>100 {values.currency}</span>
          <span>10 000 {values.currency}</span>
        </div>
      </div>

      {/* Unit Cost */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="unit-cost-input" className="text-gray-700 font-medium">{translations.unitCost}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="unit-cost-input"
              type="text"
              value={values.unitCost.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('unitCost', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.unitCost}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.unitCost / 1000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="unit-cost-slider"
            type="range"
            min="1"
            max="1000"
            step="1"
            value={values.unitCost}
            onChange={(e) => handleSliderChange('unitCost', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.unitCost} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {values.currency}</span>
          <span>1 000 {values.currency}</span>
        </div>
      </div>

      {/* Number of Contracts */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="contracts-input" className="text-gray-700 font-medium">{translations.numberOfContracts}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="contracts-input"
              type="number"
              value={values.numberOfContracts}
              onChange={(e) => handleInputChange('numberOfContracts', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.numberOfContracts}
            />
            <span className="text-gray-800 font-semibold">{translations.contractsSuffix}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(values.numberOfContracts / 100) * 100}%` }}
            ></div>
          </div>
          <input
            id="contracts-slider"
            type="range"
            min="1"
            max="100"
            step="1"
            value={values.numberOfContracts}
            onChange={(e) => handleSliderChange('numberOfContracts', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.numberOfContracts} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.contractsSuffix}</span>
          <span>100 {translations.contractsSuffix}</span>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
